import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    collection,
    getDoc,
    getDocs,
    deleteDoc,
    doc,
    updateDoc,
    addDoc,
    query,
    orderBy,
    limit,
    startAfter,
    QueryDocumentSnapshot,
    getCountFromServer,
    where,
} from 'firebase/firestore';
import { auth, db, functions } from '@/firebase';
import { httpsCallable } from 'firebase/functions';
import { formatPhoneNumber } from '../../../utils/phone';

interface SyncGroupResult {
    created: number;
    updated: number;
    errors: number;
    errorDetails: string[];
}

interface CombinedSyncResult {
    success: boolean;
    users: SyncGroupResult;
    orders: SyncGroupResult;
}

interface UserData {
    id: string;
    phoneNumber: string;
    display_name: string;
    Role: string;
    postalCode?: string;
    address?: string;
    hasMemo: boolean;
    [key: string]: any;
}

const PAGE_SIZE = 50;

export default function UserManagement() {
    const navigate = useNavigate();
    const [users, setUsers] = useState<UserData[]>([]);
    const [loading, setLoading] = useState(true);
    const [userCursors, setUserCursors] = useState<QueryDocumentSnapshot[]>([]);
    const [userPage, setUserPage] = useState(1);
    const [hasNextUserPage, setHasNextUserPage] = useState(false);
    const [userTotalCount, setUserTotalCount] = useState(0);
    const [userIdFilter, setUserIdFilter] = useState('');
    const [activeUserIdFilter, setActiveUserIdFilter] = useState('');
    const [displayNameFilter, setDisplayNameFilter] = useState('');
    const [activeDisplayNameFilter, setActiveDisplayNameFilter] = useState('');
    const [editingUser, setEditingUser] = useState<UserData | null>(null);
    const [editForm, setEditForm] = useState<Partial<UserData>>({});
    const [isAddingUser, setIsAddingUser] = useState(false);
    const [newUserForm, setNewUserForm] = useState({
        display_name: '',
        phoneNumber: '',
        Role: 'user',
        postalCode: '',
        address: ''
    });
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<CombinedSyncResult | null>(null);

    const applyUserSearch = () => {
        const trimmedUserId = userIdFilter.trim();
        const trimmedDisplayName = displayNameFilter.trim();
        setActiveUserIdFilter(trimmedUserId);
        setActiveDisplayNameFilter(trimmedDisplayName);
        setUserCursors([]);
        fetchUsers(1, trimmedUserId, trimmedDisplayName);
        fetchUserTotalCount(trimmedUserId, trimmedDisplayName);
    };

    const renderMemoBadge = (hasMemo: boolean) => {
        const badgeClass = hasMemo ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-gray-600';
        const label = hasMemo ? 'メモあり' : 'メモなし';
        return (
            <span
                className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${badgeClass}`}
                title={label}
                aria-label={label}
            >
                <i className="ri-sticky-note-line" />
            </span>
        );
    };

    const fetchUserHasMemo = async (userRef: ReturnType<typeof doc>) => {
        try {
            const memoSnapshot = await getDocs(
                query(
                    collection(db, 'Memo'),
                    where('TGTuser', '==', userRef),
                    limit(1)
                )
            );
            return !memoSnapshot.empty;
        } catch (error) {
            console.error('Error fetching memo for user', userRef.id, ':', error);
            return false;
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchUserTotalCount();
    }, []);

    const fetchUserTotalCount = async (userId: string = '', displayName: string = '') => {
        try {
            if (userId) {
                const userDoc = await getDoc(doc(db, 'User', userId));
                setUserTotalCount(userDoc.exists() ? 1 : 0);
                return;
            }

            if (displayName) {
                const snapshot = await getCountFromServer(
                    query(
                        collection(db, 'User'),
                        orderBy('display_name'),
                        where('display_name', '>=', displayName),
                        where('display_name', '<=', `${displayName}\uf8ff`)
                    )
                );
                setUserTotalCount(snapshot.data().count);
                return;
            }

            const snapshot = await getCountFromServer(collection(db, 'User'));
            setUserTotalCount(snapshot.data().count);
        } catch (error) {
            console.error('Error fetching user total count:', error);
        }
    };

    const fetchUsers = async (page: number = 1, userId: string = '', displayName: string = '') => {
        try {
            setLoading(true);

            if (userId) {
                const userDoc = await getDoc(doc(db, 'User', userId));
                const usersData = userDoc.exists()
                    ? ([
                        {
                            id: userDoc.id,
                            ...userDoc.data(),
                            hasMemo: await fetchUserHasMemo(userDoc.ref),
                        }
                    ] as UserData[])
                    : [];
                setUsers(usersData);
                setUserPage(1);
                setHasNextUserPage(false);
                setUserCursors([]);
                return;
            }

            if (displayName) {
                const querySnapshot = await getDocs(
                    query(
                        collection(db, 'User'),
                        orderBy('display_name'),
                        where('display_name', '>=', displayName),
                        where('display_name', '<=', `${displayName}\uf8ff`),
                        limit(PAGE_SIZE)
                    )
                );
                const usersData = await Promise.all(
                    querySnapshot.docs.map(async (userDoc) => ({
                        id: userDoc.id,
                        ...userDoc.data(),
                        hasMemo: await fetchUserHasMemo(userDoc.ref),
                    }))
                );
                setUsers(usersData);
                setUserPage(1);
                setHasNextUserPage(false);
                setUserCursors([]);
                return;
            }

            const baseQuery = [collection(db, 'User'), orderBy('createdAt', 'desc'), limit(PAGE_SIZE)];
            const cursor = page > 1 ? userCursors[page - 2] : undefined;
            const querySnapshot = cursor
                ? await getDocs(query(...baseQuery, startAfter(cursor)))
                : await getDocs(query(...baseQuery));

            const usersData = await Promise.all(
                querySnapshot.docs.map(async (userDoc) => ({
                    id: userDoc.id,
                    ...userDoc.data(),
                    hasMemo: await fetchUserHasMemo(userDoc.ref),
                }))
            );

            setUsers(usersData);
            setUserPage(page);
            setHasNextUserPage(querySnapshot.docs.length === PAGE_SIZE);

            if (querySnapshot.docs.length > 0) {
                setUserCursors((prev) => {
                    const updated = [...prev];
                    updated[page - 1] = querySnapshot.docs[querySnapshot.docs.length - 1];
                    return updated.slice(0, page);
                });
            }
        } catch (error: any) {
            console.error('Error fetching users:', error);
            alert(`ユーザー一覧の取得に失敗しました: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const getUserDisplayRange = () => {
        if (userTotalCount === 0) return '0件';
        const start = (userPage - 1) * PAGE_SIZE + 1;
        const end = Math.min(userPage * PAGE_SIZE, userTotalCount);
        return `${userTotalCount} 件中 ${start} ～ ${end} 件を表示中`;
    };

    const handleEdit = (user: UserData) => {
        setEditingUser(user);
        setEditForm({ ...user });
    };

    const handleUserNextPage = () => {
        if (hasNextUserPage) {
            fetchUsers(userPage + 1, activeUserIdFilter, activeDisplayNameFilter);
        }
    };

    const handleUserPrevPage = () => {
        if (userPage > 1) {
            fetchUsers(userPage - 1, activeUserIdFilter, activeDisplayNameFilter);
        }
    };

    const handleSaveEdit = async () => { 
        if (!editingUser) return;

        try {
            const { id, ...updateData } = editForm;
            await updateDoc(doc(db, 'User', editingUser.id), updateData);
            alert('ユーザー情報を更新しました');
            setEditingUser(null);
            setEditForm({});
            fetchUsers(userPage, activeUserIdFilter, activeDisplayNameFilter);
        } catch (error) {
            console.error('Error updating user:', error);
            alert(`更新に失敗しました: ${error}`);
        }
    };

    const callSyncAllFromSheets = async (): Promise<CombinedSyncResult> => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('ログインが必要です');
        }

        const syncAllFromSheets = httpsCallable(functions, 'syncAllFromSheets');
        // URL 直叩き＋手動トークン付与を廃止し、callable 経由で環境に応じた Functions を利用
        const response = await syncAllFromSheets();

        const responseData = response.data as CombinedSyncResult;
        if (!responseData?.success) {
            const message = (responseData as any)?.error || 'Functions error';
            throw new Error(message as string);
        }

        return responseData;
    };

    const handleAddUser = async () => {
        if (!newUserForm.display_name || !newUserForm.phoneNumber) {
            alert('表示名と電話番号は必須です');
            return;
        }

        try {
            await addDoc(collection(db, 'User'), {
                display_name: newUserForm.display_name,
                phoneNumber: newUserForm.phoneNumber,
                Role: newUserForm.Role,
                postalCode: newUserForm.postalCode,
                address: newUserForm.address
            });

            alert('ユーザーを追加しました');
            setIsAddingUser(false);
            setNewUserForm({ display_name: '', phoneNumber: '', Role: 'user', postalCode: '', address: '' });
            fetchUsers(userPage, activeUserIdFilter, activeDisplayNameFilter);
            fetchUserTotalCount(activeUserIdFilter, activeDisplayNameFilter);
        } catch (error) {
            console.error('Error adding user:', error);
            alert(`追加に失敗しました: ${error}`);
        }
    };

    const handleDelete = async (userId: string) => {
        if (!confirm('このユーザーを削除してもよろしいですか？')) return;

        try {
            await deleteDoc(doc(db, 'User', userId));
            alert('ユーザーを削除しました');
            fetchUsers(userPage, activeUserIdFilter, activeDisplayNameFilter);
            fetchUserTotalCount(activeUserIdFilter, activeDisplayNameFilter);
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('削除に失敗しました');
        }
    };

    const handleLogout = async () => {
        try {
            await auth.signOut();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const searchAddress = async (zipcode: string, isEdit: boolean) => {
        if (!zipcode) return;

        try {
            const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zipcode}`);
            const data = await response.json();

            if (data.results) {
                const result = data.results[0];
                const fullAddress = `${result.address1}${result.address2}${result.address3}`;

                if (isEdit) {
                    setEditForm(prev => ({ ...prev, address: fullAddress }));
                } else {
                    setNewUserForm(prev => ({ ...prev, address: fullAddress }));
                }
            } else {
                alert('住所が見つかりませんでした');
            }
        } catch (error) {
            console.error('Error searching address:', error);
            alert('住所検索に失敗しました');
        }
    };

    const handleSync = async () => {
        if (!confirm('スプレッドシートからデータを同期しますか?\n既存のデータは上書きされます。')) return;

        setIsSyncing(true);
        try {
            const data = await callSyncAllFromSheets();
            setSyncResult(data);
            alert(
                `同期完了!\n[Users] 新規作成: ${data.users.created}件 / 更新: ${data.users.updated}件 / エラー: ${data.users.errors}件` +
                `\n[Order] 新規作成: ${data.orders.created}件 / 更新: ${data.orders.updated}件 / エラー: ${data.orders.errors}件`
            );

            fetchUsers(userPage, activeUserIdFilter, activeDisplayNameFilter);
            fetchUserTotalCount(activeUserIdFilter, activeDisplayNameFilter);
        } catch (error: any) {
            console.error('Sync error:', error);
            alert(`同期に失敗しました: ${error.message}`);
        } finally {
            setIsSyncing(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-lg">読み込み中...</div>
            </div>
        );
    }

    return (
        <div className="">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">ユーザー管理</h1>
                        <p className="text-gray-600">登録されているユーザーの一覧</p>
                        <p className="text-sm text-gray-500 mt-1">※ 資格の紐付けはOrder管理ページで行います</p>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={handleSync}
                            disabled={isSyncing}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSyncing ? (
                                <>
                                    <span className="animate-spin">⟳</span>
                                    同期中...
                                </>
                            ) : (
                                <>
                                    📊 スプレッドシートから同期
                                </>
                            )}
                        </button>
                        <button
                            onClick={() => setIsAddingUser(true)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            ＋ ユーザー追加
                        </button>
                    </div>
                </div>

                <div className="mb-4 text-sm text-gray-600 font-medium">
                    {getUserDisplayRange()}
                </div>

                <div className="mb-4 flex flex-wrap items-end gap-3">
                    <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700" htmlFor="userIdFilter">
                            UID（完全一致）
                        </label>
                        <input
                            id="userIdFilter"
                            type="text"
                            value={userIdFilter}
                            onChange={(e) => setUserIdFilter(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    applyUserSearch();
                                }
                            }}
                            placeholder="例: bIXAP2UDlg9UMNsrFLQW"
                            className="mt-1 w-72 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700" htmlFor="displayNameFilter">
                            表示名（前方一致）
                        </label>
                        <input
                            id="displayNameFilter"
                            type="text"
                            value={displayNameFilter}
                            onChange={(e) => setDisplayNameFilter(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    applyUserSearch();
                                }
                            }}
                            placeholder="例: 山田 太郎"
                            className="mt-1 w-72 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <button
                        onClick={applyUserSearch}
                        disabled={loading}
                        className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                    >
                        検索
                    </button>
                    <button
                        onClick={() => {
                            setUserIdFilter('');
                            setActiveUserIdFilter('');
                            setDisplayNameFilter('');
                            setActiveDisplayNameFilter('');
                            setUserCursors([]);
                            fetchUsers(1, '', '');
                            fetchUserTotalCount('', '');
                        }}
                        disabled={
                            loading ||
                            (!userIdFilter && !activeUserIdFilter && !displayNameFilter && !activeDisplayNameFilter)
                        }
                        className="h-10 rounded-lg bg-gray-200 px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        クリア
                    </button>
                </div>

                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-100 border-b">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">ID</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">表示名</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">電話番号</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">権限</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">郵便番号</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">住所</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">操作</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {users.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-600 font-mono text-xs">{user.id}</td>
                                        <td className="px-6 py-4 text-sm text-gray-800">
                                            <div className="flex items-center gap-2">
                                                {renderMemoBadge(user.hasMemo)}
                                                <Link
                                                    to={`/admin/users/${user.id}`}
                                                    className="text-emerald-600 font-semibold hover:underline"
                                                >
                                                    {user.display_name || '-'}
                                                </Link>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-800">{formatPhoneNumber(user.phoneNumber)}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 text-xs rounded-full ${user.Role === 'admin'
                                                ? 'bg-purple-100 text-purple-700'
                                                : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                {user.Role || 'user'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-800">{user.postalCode || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-800">{user.address || '-'}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleEdit(user)}
                                                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                                                >
                                                    編集
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(user.id)}
                                                    className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                                                >
                                                    削除
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {users.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            ユーザーが登録されていません
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between mt-4">
                    <button
                        onClick={handleUserPrevPage}
                        disabled={userPage === 1 || loading}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        前のページ
                    </button>
                    <div className="text-sm text-gray-700 font-medium">Page {userPage}</div>
                    <button
                        onClick={handleUserNextPage}
                        disabled={!hasNextUserPage || loading}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        次のページ
                    </button>
                </div>

                {/* Sync Result Modal */}
                {syncResult && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold text-gray-800">同期結果</h2>
                                <span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-700">一括同期</span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {[{ title: 'Users', result: syncResult.users }, { title: 'Order', result: syncResult.orders }].map(({ title, result }) => (
                                    <div key={title} className="border rounded-lg p-4 bg-gray-50">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-3">{title}</h3>
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-700">新規作成:</span>
                                                <span className="font-bold text-green-600">{result.created}件</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-700">更新:</span>
                                                <span className="font-bold text-blue-600">{result.updated}件</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-700">エラー:</span>
                                                <span className="font-bold text-red-600">{result.errors}件</span>
                                            </div>
                                        </div>

                                        {result.errorDetails && result.errorDetails.length > 0 && (
                                            <div className="mt-4">
                                                <p className="text-sm font-medium text-gray-700 mb-2">エラー詳細:</p>
                                                <div className="bg-red-50 border border-red-200 rounded p-3 max-h-32 overflow-y-auto">
                                                    {result.errorDetails.map((error: string, index: number) => (
                                                        <p key={index} className="text-sm text-red-700">{error}</p>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => setSyncResult(null)}
                                className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {editingUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-2xl font-bold text-gray-800">ユーザー編集</h2>
                            <p className="text-sm text-gray-600 mt-1">ID: {editingUser.id}</p>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">表示名</label>
                                <input
                                    type="text"
                                    value={editForm.display_name || ''}
                                    onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">電話番号</label>
                                <input
                                    type="text"
                                    value={editForm.phoneNumber || ''}
                                    onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">郵便番号</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={editForm.postalCode || ''}
                                            onChange={(e) => setEditForm({ ...editForm, postalCode: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="1234567"
                                        />
                                        <button
                                            onClick={() => searchAddress(editForm.postalCode || '', true)}
                                            className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
                                        >
                                            検索
                                        </button>
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">住所</label>
                                    <input
                                        type="text"
                                        value={editForm.address || ''}
                                        onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">権限</label>
                                <select
                                    value={editForm.Role || 'user'}
                                    onChange={(e) => setEditForm({ ...editForm, Role: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="user">user</option>
                                    <option value="admin">admin</option>
                                </select>
                            </div>
                        </div>

                        <div className="p-6 border-t flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setEditingUser(null);
                                    setEditForm({});
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={handleSaveEdit}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                保存
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add User Modal */}
            {isAddingUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-2xl font-bold text-gray-800">ユーザー追加</h2>
                            <p className="text-sm text-gray-600 mt-1">新しいユーザーを登録します</p>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    表示名 <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newUserForm.display_name}
                                    onChange={(e) => setNewUserForm({ ...newUserForm, display_name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="山田太郎"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    電話番号 <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newUserForm.phoneNumber}
                                    onChange={(e) => setNewUserForm({ ...newUserForm, phoneNumber: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="+819012345678"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">郵便番号</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={newUserForm.postalCode}
                                            onChange={(e) => setNewUserForm({ ...newUserForm, postalCode: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="1234567"
                                        />
                                        <button
                                            onClick={() => searchAddress(newUserForm.postalCode, false)}
                                            className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
                                        >
                                            検索
                                        </button>
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">住所</label>
                                    <input
                                        type="text"
                                        value={newUserForm.address}
                                        onChange={(e) => setNewUserForm({ ...newUserForm, address: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">権限</label>
                                <select
                                    value={newUserForm.Role}
                                    onChange={(e) => setNewUserForm({ ...newUserForm, Role: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="user">user</option>
                                    <option value="admin">admin</option>
                                </select>
                            </div>
                        </div>

                        <div className="p-6 border-t flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setIsAddingUser(false);
                                    setNewUserForm({ display_name: '', phoneNumber: '', Role: 'user', postalCode: '', address: '' });
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={handleAddUser}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                                追加
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
